import discoveredProjectImages from "virtual:project-images";

const normalizeProjectName = (value) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const describeScreenshot = (projectTitle, filename) => {
  const description = filename
    .replace(/\.[^.]+$/, "")
    .replace(/[-_]+/g, " ")
    .replace(
      new RegExp(
        `^${projectTitle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*`,
        "i",
      ),
      "",
    )
    .trim();

  return `${projectTitle}${description ? ` ${description}` : ""} screenshot`;
};

const includeDiscoveredImages = (project) => {
  const folderName = Object.keys(discoveredProjectImages).find(
    (folder) =>
      normalizeProjectName(folder) ===
      normalizeProjectName(project.imageFolder ?? project.title),
  );
  const discovered = folderName ? discoveredProjectImages[folderName] : [];
  const curated = project.images ?? [];
  const curatedSources = new Set(
    curated.map((image) => decodeURI(image.src).toLowerCase()),
  );
  const additions = discovered
    .filter((image) => !curatedSources.has(decodeURI(image.src).toLowerCase()))
    .map((image) => ({
      src: image.src,
      alt: describeScreenshot(project.title, image.filename),
    }));
  const images = [...curated, ...additions];

  return images.length ? { ...project, images } : project;
};

export const portfolio = {
  version: "1.8.1",
  profile: {
    name: "KeCheng",
    shortName: "KeCheng",
    initials: "KQ",
    role: ["Full-stack", "developer"],
    introduction:
      "I build thoughtful digital products from interface to infrastructure, with a focus on clarity, performance, and useful detail.",
    email: "hi@kecheng.dev",
    location: "San Francisco, California",
    availability: "Available for select opportunities",
  },
  navigation: [
    { label: "Home", id: "home" },
    { label: "About", id: "about" },
    { label: "Stack", id: "stack" },
    { label: "Experience", id: "experience" },
    { label: "Projects", id: "projects" },
    { label: "Contact", id: "contact" },
  ],
  stats: [
    { value: "05+", label: "Years building" },
    { value: "10+", label: "Projects completed" },
    { value: "SF", label: "Based in California" },
  ],
  about: {
    statement:
      "I believe useful software begins with the people who will rely on it, then earns their trust through clarity, speed, and thoughtful detail.",
    title: "Engineer with a product mindset.",
    introduction: "Hi, I am KeCheng.",
    paragraphs: [
      "I work across the stack, translating complicated requirements into digital products that feel calm and direct. I care equally about the interface people touch and the systems that keep it dependable.",
      "My approach combines practical engineering with close attention to hierarchy, accessibility, and performance. The technology should support the experience, never compete with it.",
    ],
  },
  stack: [
    {
      title: "Frontend",
      skills: [
        { name: "React", icon: "/tech-icons/react.svg" },
        { name: "JavaScript", icon: "/tech-icons/javascript.svg" },
        { name: "Angular", icon: "/tech-icons/angular.svg" },
        { name: "HTML", icon: "/tech-icons/html5.svg" },
        { name: "CSS", icon: "/tech-icons/css.svg" },
        { name: "Vite", icon: "/tech-icons/vite.svg" },
        { name: "Next.js", icon: "/tech-icons/nextdotjs.svg" },
        { name: "Tailwind CSS", icon: "/tech-icons/tailwindcss.svg" },
      ],
    },
    {
      title: "Backend",
      skills: [
        { name: "Node.js", icon: "/tech-icons/nodedotjs.svg" },
        { name: "Python", icon: "/tech-icons/python.svg" },
        { name: "Java", icon: "/tech-icons/openjdk.svg" },
        { name: "Spring Boot", icon: "/tech-icons/springboot.svg" },
      ],
    },
    {
      title: "Database",
      skills: [
        { name: "MongoDB", icon: "/tech-icons/mongodb.svg" },
        { name: "MySQL", icon: "/tech-icons/mysql.svg" },
        { name: "Oracle Database", icon: "/tech-icons/oracle.svg" },
      ],
    },
    {
      title: "Tools",
      skills: [
        { name: "Git", icon: "/tech-icons/git.svg" },
        { name: "Docker", icon: "/tech-icons/docker.svg" },
        { name: "Cloudflare", icon: "/tech-icons/cloudflare.svg" },
        { name: "GitHub Actions", icon: "/tech-icons/githubactions.svg" },
        { name: "Karate", icon: "/tech-icons/karate.svg" },
        { name: "GSAP", icon: "/tech-icons/gsap.svg" },
        { name: "Lenis", icon: "/tech-icons/lenis.svg" },
        { name: "Vitest", icon: "/tech-icons/vitest.svg" },
        { name: "Vercel", icon: "/tech-icons/vercel.svg" },
      ],
    },
  ],
  experience: [
    {
      period: "2022 - Present",
      company: "Wells Fargo",
      role: "Software Engineer",
      detail:
        "Build and maintain backend systems that support home mortgage payments, improving reliability and the customer experience across home lending.",
    },
    {
      period: "2020 - 2022",
      company: "Independent / Selected collaborations",
      role: "Full-stack Developer",
      detail:
        "Designed and built dependable modern applications across frontend, backend, and deployment workflows.",
    },
    {
      period: "Ongoing",
      company: "Personal projects / Applied learning",
      role: "Full-stack Systems",
      detail:
        "Design and build end-to-end projects to deepen my practice in API design, data modeling, testing, deployment, and user-centered interfaces.",
    },
  ],
  projects: [
    {
      number: "01",
      title: "FundPulse",
      eyebrow: "ETF portfolio intelligence",
      description:
        "A responsive research workspace for tracking ETF performance, comparing allocations, and spotting portfolio signals at a glance.",
      stack: ["Next.js", "React", "Tailwind CSS", "Vercel"],
      image: "/project-covers/FundPulse/fundpulse-cover.webp",
      imageAlt:
        "FundPulse product overview with ETF analytics, performance chart, and allocation tools",
      imageWidth: 1600,
      imageHeight: 900,
      images: [
        {
          src: "/project-covers/FundPulse/fundpulse-overview.webp",
          alt: "FundPulse landing page with ETF portfolio value, performance chart, and rebalancing alerts",
          width: 2560,
          height: 1023,
        },
        {
          src: "/project-covers/FundPulse/fundpulse-analytics.webp",
          alt: "FundPulse analytics view with sector allocation, expense ratios, rebalancing alerts, and asset breakdown",
          width: 2550,
          height: 1207,
        },
      ],
      href: null,
      caseStudy: {
        intro:
          "A redesigned ETF research experience that brings performance, allocation, cost, and rebalancing signals into one responsive workspace.",
        challenge:
          "The earlier dashboard surfaced useful portfolio data, but its dense controls and separate views made the overall story harder to scan, especially across screen sizes.",
        approach: [
          "Reframe the product around a clear portfolio overview, then progressively reveal fund-level analytics and holdings detail.",
          "Build reusable Next.js and React components for performance metrics, allocation views, and responsive data states.",
          "Use Tailwind CSS to keep the visual system consistent and deploy the redesigned experience through Vercel.",
        ],
        result:
          "The redesign creates a cohesive ETF research surface that is easier to scan, navigate, and extend as new portfolio insights are added.",
      },
    },
    {
      number: "02",
      title: "Local Control",
      eyebrow: "Private service orchestration",
      description:
        "A mobile-first control surface for monitoring, launching, and stopping local Next.js services securely over Tailscale.",
      stack: ["Next.js", "React", "Node.js", "Tailscale"],
      image: "/project-covers/LocalControl/localcontrol-coverpage.webp",
      imageAlt:
        "Local Control dashboard showing service health, automation status, and launch controls",
      imageWidth: 900,
      imageHeight: 1261,
      imagePosition: "center top",
      imageFolder: "LocalControl",
      images: [
        {
          src: "/project-covers/LocalControl/localcontrol-coverpage.webp",
          alt: "Local Control desktop dashboard with service health and process controls",
          width: 900,
          height: 1261,
          position: "center top",
        },
        {
          src: "/project-covers/LocalControl/localcontrol-mobile.webp",
          alt: "Local Control responsive mobile dashboard views",
          width: 1202,
          height: 999,
          position: "center top",
        },
      ],
      href: "https://github.com/kcqiu/local-control",
      linkLabel: "View source on GitHub",
      caseStudy: {
        intro:
          "A private operations dashboard that turns an iPhone into a focused control surface for local Next.js services connected through Tailscale.",
        challenge:
          "Monitoring and controlling several local services from a phone required a secure, responsive interface without hard-coding any project into the dashboard.",
        approach: [
          "Generate every service card from environment variables so new projects can be added without changing application code.",
          "Use a custom Next.js server to read health routes and ports, report live status, and manage Windows launchers safely.",
          "Bind access through Tailscale, protect state-changing actions, and add ntfy alerts plus iPhone safe-area support for dependable mobile operation.",
        ],
        result:
          "Local Control provides one private, extensible command center for service health, response time, automation state, notifications, and process controls.",
      },
    },
    {
      number: "03",
      title: "MISE",
      eyebrow: "Smart culinary workspace",
      description:
        "A full-stack culinary workspace featuring multimodal Gemini API intake, cross-device Supabase sync, department-sorted grocery planning, and dynamic cooking controls.",
      stack: ["React", "Vite", "Supabase", "Vercel", "Tailwind CSS", "Gemini API"],
      image: "/project-covers/RecipeShelf/recipe-shelf-cover.webp",
      imageAlt:
        "MISE culinary workspace with recipe shelf, cooking mode, and grocery planning",
      imageWidth: 1600,
      imageHeight: 900,
      imageFolder: "RecipeShelf",
      images: [
        {
          src: "/project-covers/RecipeShelf/recipe-shelf-desktop.webp",
          alt: "MISE recipe shelf desktop view with category filters, quick search, and editorial recipe cards",
          width: 2880,
          height: 1800,
        },
        {
          src: "/project-covers/RecipeShelf/recipe-shelf-mobile.webp",
          alt: "MISE mobile cookbook view on smartphone with touch-friendly navigation and stacked recipe cards",
          width: 786,
          height: 1704,
          position: "center top",
        },
        {
          src: "/project-covers/RecipeShelf/recipe-cooking-mode-desktop.webp",
          alt: "Interactive cooking mode with dynamic serving scaling, unit conversions, progress checklist, and keep-screen-awake toggle",
          width: 2880,
          height: 1800,
        },
        {
          src: "/project-covers/RecipeShelf/recipe-detail-mobile.webp",
          alt: "Mobile recipe detail view with dish photography, prep timings, and cooking progress",
          width: 786,
          height: 1704,
          position: "center top",
        },
        {
          src: "/project-covers/RecipeShelf/recipe-grocery-desktop.webp",
          alt: "Smart grocery list generated from active recipes with ingredient categories and purchase progress",
          width: 2880,
          height: 1800,
        },
        {
          src: "/project-covers/RecipeShelf/recipe-grocery-mobile.webp",
          alt: "Mobile supermarket grocery checklist with checkable items sorted by aisle",
          width: 786,
          height: 1704,
          position: "center top",
        },
        {
          src: "/project-covers/RecipeShelf/recipe-intake-modal.webp",
          alt: "Gemini API recipe intake modal supporting photo scanning, web link importing, and video extraction",
          width: 2880,
          height: 1800,
        },
      ],
      href: "https://mise.kecheng.dev",
      linkLabel: "Open live app at mise.kecheng.dev",
      caseStudy: {
        intro:
          "A full-stack culinary workspace designed for real kitchen workflows, featuring Gemini API recipe ingestion from photos and web links, real-time cross-device sync via Supabase, and automated department-sorted grocery planning.",
        challenge:
          "Traditional recipe sites are bloated with ads and narrative filler, while cooking apps often lack seamless ways to import recipes from photos or social videos and keep grocery trips synchronized across household devices.",
        approach: [
          "Deploy Vercel serverless functions with the Google Gemini API to extract structured ingredients and steps from cookbook photos, web URLs, and social video links.",
          "Architect a Supabase backend with Row-Level Security, optimistic concurrency control, and realtime subscriptions for instant cross-device recipe and grocery synchronization.",
          "Build a tactile, kitchen-tested interface with React 19, Vite, and Tailwind CSS featuring proportional serving scaling, unit conversions, and screen wake lock.",
        ],
        result:
          "MISE provides a calm, high-utility cooking companion with instant multimodal Gemini API intake, automated supermarket grocery checklists, and cloud-synced recipe management live at mise.kecheng.dev.",
      },
    },
    {
      number: "04",
      title: "Portfolio System",
      eyebrow: "Identity and interface",
      description:
        "A performance-focused personal website and interactive design system featuring a 3D GSAP card fan carousel, smooth drag gestures, and accessible responsive engineering.",
      stack: ["React 19", "Vite", "Tailwind CSS", "GSAP", "Motion", "Vitest", "Cloudflare Pages"],
      image: "/project-covers/PortfolioSystem/portfolio-cover.webp",
      imageAlt:
        "KeCheng portfolio homepage with full-stack developer heading and project showcase",
      imageWidth: 2880,
      imageHeight: 1800,
      imageFolder: "PortfolioSystem",
      images: [
        {
          src: "/project-covers/PortfolioSystem/portfolio-desktop.webp",
          alt: "KeCheng portfolio homepage desktop view with full-stack developer heading and status indicators",
          width: 2880,
          height: 1800,
        },
        {
          src: "/project-covers/PortfolioSystem/portfolio-mobile.webp",
          alt: "Portfolio mobile view on smartphone with responsive navigation and hero layout",
          width: 1179,
          height: 2556,
          position: "center top",
        },
        {
          src: "/project-covers/PortfolioSystem/portfolio-about-desktop.webp",
          alt: "About section desktop view with editorial statement, personal philosophy, and interactive typed identity",
          width: 2880,
          height: 1800,
        },
        {
          src: "/project-covers/PortfolioSystem/portfolio-about-mobile.webp",
          alt: "About section mobile view with fluid typography and touch-optimized layout",
          width: 1179,
          height: 2556,
          position: "center top",
        },
        {
          src: "/project-covers/PortfolioSystem/portfolio-stack-desktop.webp",
          alt: "Technical stack grid on desktop grouped by Frontend, Backend, Database, and Developer Tools",
          width: 2880,
          height: 1800,
        },
        {
          src: "/project-covers/PortfolioSystem/portfolio-stack-mobile.webp",
          alt: "Technical stack mobile view with categorized icons and responsive layout",
          width: 1179,
          height: 2556,
          position: "center top",
        },
        {
          src: "/project-covers/PortfolioSystem/portfolio-experience-desktop.webp",
          alt: "Experience section desktop timeline highlighting engineering roles and system achievements",
          width: 2880,
          height: 1800,
        },
        {
          src: "/project-covers/PortfolioSystem/portfolio-experience-mobile.webp",
          alt: "Experience section mobile timeline with clear chronological hierarchy",
          width: 1179,
          height: 2556,
          position: "center top",
        },
        {
          src: "/project-covers/PortfolioSystem/portfolio-projects-desktop.webp",
          alt: "Selected projects section on desktop with interactive project preview and metadata",
          width: 2880,
          height: 1800,
        },
        {
          src: "/project-covers/PortfolioSystem/portfolio-projects-mobile.webp",
          alt: "Selected projects mobile list view with responsive project cards",
          width: 1179,
          height: 2556,
          position: "center top",
        },
      ],
      href: "https://kecheng.dev",
      linkLabel: "Open live site at kecheng.dev",
      caseStudy: {
        intro:
          "A personal identity and design system combining editorial typography, an interactive 21st.dev Card Fan Carousel with GSAP & Motion, and clean static deployment.",
        challenge:
          "Present full-stack engineering experience with an expressive, cinematic visual design while maintaining 60fps animations, touch/drag responsiveness, and sub-second load times.",
        approach: [
          "Integrate an interactive 21st.dev Card Fan Carousel with GSAP transform ownership, elastic hover fanning, and Motion swipe momentum.",
          "Adopt a hybrid Tailwind CSS v4 architecture for utility styling without disrupting bespoke CSS variables or theme tokens.",
          "Build accessible modal and lightbox experiences with keyboard traps, escape exits, and automated Vitest regression tests.",
          "Ship a static Vite bundle deployed through GitHub Actions and Cloudflare Pages.",
        ],
        result:
          "An accessible, high-performance portfolio system pairing fluid 60fps animations with responsive design, automated test suites, and sub-second page loads.",
      },
    },
  ].map(includeDiscoveredImages),
  socials: [
    { label: "GitHub", href: "https://github.com/kcqiu" },
    { label: "Email", href: "mailto:hi@kecheng.dev" },
  ],
};
